import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Bot, ArrowRight } from "lucide-react";
import { seoPages, seoClusters, SITE } from "../data/seoPages";

export default function SoftwareHubPage() {
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE },
          { "@type": "ListItem", position: 2, name: "Software", item: `${SITE}/software` },
        ],
      },
      {
        "@type": "ItemList",
        name: "BrandBorn AI Software & Marketplace Services",
        itemListElement: seoPages.map((p, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: p.kw,
          url: `${SITE}/software/${p.slug}`,
        })),
      },
      {
        "@type": "SoftwareApplication",
        name: "EcomCatalog.ai",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        url: "https://ecomcatalog.ai",
        description: "India's first AI auto listings and data analysis software for e-commerce sellers. Generates marketplace-ready listings in 90 seconds for 70+ Indian platforms.",
        creator: { "@type": "Organization", name: "BrandBorn", url: SITE },
        aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", ratingCount: "1000", bestRating: "5" },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white pt-24" data-testid="software-hub-page">
      <Helmet>
        <title>AI Ecommerce Software Suite | Listings, Catalogs & Analytics — BrandBorn</title>
        <meta name="description" content="Explore BrandBorn's AI software suite: AI listing software, catalog automation, PIM, marketplace analytics and onboarding services for Myntra, Amazon, Flipkart, Blinkit & 70+ platforms." />
        <link rel="canonical" href={`${SITE}/software`} />
        <meta property="og:title" content="AI Ecommerce Software Suite — BrandBorn & EcomCatalog.ai" />
        <meta property="og:description" content="AI listings, catalog automation, analytics and marketplace onboarding for Indian sellers." />
        <meta property="og:url" content={`${SITE}/software`} />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <section className="px-4 py-14 md:py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-100 text-blue-700 rounded-full text-xs font-bold mb-5">
            <Bot size={14} /> INDIA'S FIRST AI LISTING SOFTWARE — ECOMCATALOG.AI
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-4">
            AI Software for Marketplace Sellers
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-4">
            AI listings, catalog automation, product data management and marketplace analytics — built by BrandBorn, India's #1 onboarding agency and Official Myntra OMS Partner.
          </p>
          <p className="text-sm text-gray-400 font-semibold">1000+ brands • 98% approval rate • 70+ marketplaces • Featured in Times of India & Economic Times</p>
        </div>
      </section>

      {seoClusters.map((cluster) => (
        <section key={cluster} className="px-4 py-8">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-black text-gray-900 mb-5">{cluster}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {seoPages.filter((p) => p.cluster === cluster).map((p) => (
                <Link key={p.slug} to={`/software/${p.slug}`} className="group p-5 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 hover:border-red-200 transition-all" data-testid={`hub-card-${p.slug}`}>
                  <h3 className="font-black text-gray-900 mb-2 flex items-center justify-between gap-2">
                    {p.kw}
                    <ArrowRight size={16} className="text-red-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                  </h3>
                  <p className="text-xs text-gray-500 leading-relaxed line-clamp-3">{p.sub}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ))}

      <section className="px-4 py-10">
        <div className="max-w-5xl mx-auto flex flex-wrap gap-3 text-sm font-bold">
          <Link to="/knowledge" className="text-blue-600 hover:underline">Knowledge Base →</Link>
          <Link to="/services" className="text-blue-600 hover:underline">Onboarding Services →</Link>
          <Link to="/blog" className="text-blue-600 hover:underline">Blog →</Link>
          <Link to="/faq" className="text-blue-600 hover:underline">FAQs →</Link>
        </div>
      </section>

      <section className="px-4 py-14 bg-gray-950">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-black text-white mb-4">Create Your First AI Listing in 90 Seconds</h2>
          <a href="https://ecomcatalog.ai" target="_blank" rel="noopener noreferrer" className="inline-block px-8 py-4 bg-blue-600 text-white rounded-2xl font-black text-sm uppercase tracking-wider hover:bg-blue-700 hover:-translate-y-0.5 transition-all shadow-xl">Try EcomCatalog.ai Free</a>
        </div>
      </section>
    </div>
  );
}
